import { useContext, useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Card, Typography, Box } from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';

import styles from '../../../../assets/styles/componentStyles/templateLibraryStyles/whatsappTemplatePreview.module.scss';
import whatsAppDefaultBgImg from "../../../../assets/images/chatWidgetComponents/WhatsAppDefaultBg.png";

import { getWabaName } from '../../../../utils/constants';
import { IntegrationContext, IntegrationContextPayload } from '../../../../context/integrationContext';
import { useTemplateLibraryStore } from '../../../../store/templateLibraryStore';
import { getSubButtonDescription, getSubButtonIcon, getSubButtonText, getTooltipTextForMessageGuidelines, subButtonTypeIconMap } from '../constant';

import TouchTooltip from '../utils/TouchTooltip';
import WhatsAppMessageHeaderPreview from './WhatsAppMessageHeaderPreview';
import WhatsAppMessageBodyPreview from './WhatsAppMessageBodyPreview';
import WhatsAppMessageFooterPreview from './WhatsAppMessageFooterPreview';
import WhatsAppMessageButtonsPreview from './WhatsAppMessageButtonsPreview';
import { isTemplateCategoryAuthentication, needToShowHeader, shouldShowWhatsAppTemplateButtonsPreview } from '../../channels/global/whatsapp/templates/utils';
import { useShallow } from 'zustand/react/shallow';
import { useCarouselFunctions } from '../utils/useCarouselFunctions';
import { getDynamicColorsBySource } from '../../../../pages/ruleBasedInbox/utils';
import { renderUserAvatarDp } from '../../../../pages/ruleBasedInbox/renderUtils';

const WhatsAppTemplatePreview = (props: any) => {

  const { selectedTemplate, compact = false, mobile = false, editable = false, handleChangeText, handleAddBodyVariableManually, 
    removeVariableData, handleChange, handleRemoveField, headerTextareaRef, uploadFileHandler, removeFileHandler, 
    textareaRef, onChangeOfHeader, handleClick, checkDisableAddButton, calculateMenuPosition, getMenuDisableFlag, handleClose, 
    handleAddActionsButton, renderCustomButtonField, getInput, flagShowVariableTooltipForVisitWebsite, handleDeleteActionBtn, 
    setOnChangeOfPayloadObject, checkForAddDynamicVar, getWebsiteUrlValue, handleChangeCallField, handleChangeFieldVal, 
    isFlowButton, setPayloadOnChangeOfGetInput, handleSampleTextChange, anchorEl, menuPosition, inValidId, carouselInvalidId, 
    isFileUploaded, handleExpirationChange, handleSecurityRecommendationChange, handleFormatting, handleAddExpiryTimeChange } = props;

    const { isMobileScreen } = useCarouselFunctions();

  const [searchParams] = useSearchParams();
  const id = searchParams.get('id');

  const { createTemplatePayload, templateLibraryCategoryList, whatsappTemplateButtonGenericData, interactiveTypeList, whatsappTemplateTypes } = useTemplateLibraryStore(useShallow((state) => ({
    createTemplatePayload: state.createTemplatePayload,
    templateLibraryCategoryList: state.templateLibraryCategoryList,
    whatsappTemplateButtonGenericData: state.whatsappTemplateButtonGenericData,
    interactiveTypeList: state.interactiveTypeList,
    whatsappTemplateTypes: state.whatsappTemplateTypes,
  })));

  const { integrationListNewStructure } = useContext(IntegrationContext) as IntegrationContextPayload;
  const [wabaName, setWabaName] = useState<string>('');
  const previewContainerRef = useRef<HTMLDivElement>(null);

  const displayTemplate = editable ? createTemplatePayload : selectedTemplate; 

  useEffect(() => {
    if (id) {
      setWabaName(getWabaName(integrationListNewStructure, id) ?? "WhatsApp Business");
    }
    else {
      setWabaName("WhatsApp Business");
    }
  }, [integrationListNewStructure, id]);

  if (!displayTemplate) {
    return <Typography>Select a template to preview</Typography>;
  }

  const data = displayTemplate?.whatsapp_template_data;
  const bodyVariableData = data?.whatsapp_template_body?.whatsapp_template_body_variable_data ?? [];
  const headerVariableData = data?.whatsapp_template_header?.whatsapp_template_header_data?.[0]?.whatsapp_template_header_variable_data ?? [];
  
  const templateForBubble = data
    ? {
        headerType: data?.whatsapp_template_header?.whatsapp_template_header_type_name?.toLowerCase() ?? 'none',
        header: {
          type: data?.whatsapp_template_header?.whatsapp_template_header_type_name?.toLowerCase() ?? 'none',
          content: data?.whatsapp_template_header?.whatsapp_template_header_data?.[0]?.whatsapp_template_header_data_value,
          google_storage_id: data?.whatsapp_template_header?.whatsapp_template_header_data?.[0]?.whatsapp_template_header_data_google_storage_id,
          variableData: headerVariableData,
        },
        body: data?.whatsapp_template_body?.whatsapp_template_body_value ?? '',
        bodyVariableData,
        footer: data?.whatsapp_template_footer?.whatsapp_template_footer_value ?? '',
        buttons: (data?.whatsapp_template_button ?? []).map((b: any) => {
          const subBtnData = b?.whatsapp_template_button_data?.[0];
          const text = getSubButtonText(subBtnData) ?? '';
          const icon = (subBtnData?.whatsapp_template_button_sub_type_name) 
            ? subButtonTypeIconMap[subBtnData?.whatsapp_template_button_sub_type_name?.toLowerCase() as keyof typeof subButtonTypeIconMap]
            : (whatsappTemplateButtonGenericData && getSubButtonIcon(subBtnData?.whatsapp_template_button_sub_type_id, whatsappTemplateButtonGenericData));
          const btnText = subBtnData?.whatsapp_template_button_sub_type_name;
          const description = getSubButtonDescription(subBtnData);
          const isCta = b?.whatsapp_template_button_type_id === 2;
          return {
            type: (isCta ? 'cta' : 'quick_reply') as 'cta' | 'quick_reply',
            text,
            icon,
            btnText,
            description,
          };
        }),
      }
    : displayTemplate;

  const isAuthenticationTemplate = isTemplateCategoryAuthentication(templateLibraryCategoryList, createTemplatePayload?.whatsapp_template_data?.whatsapp_template_category_id);
  
  const templateButtonsCount = templateForBubble?.buttons?.length ?? 0;
  const showButtonsPreview = editable || shouldShowWhatsAppTemplateButtonsPreview(displayTemplate, interactiveTypeList, templateButtonsCount, whatsappTemplateTypes ?? []);

  return (
    <Card 
      className={`
        ${styles.whatsappPreview} 
        ${editable ? styles['whatsappPreview--editable'] : ''} 
        ${compact ? styles['whatsappPreview--compact'] : ''} 
        ${mobile ? styles['whatsappPreview--mobile'] : ''}
      `} 
      sx={{ position: 'relative', overflow: mobile ? 'hidden' : 'visible' }}
    >
      <Box
        ref={previewContainerRef}
        className={styles.previewContainer}
      >
      <Box
        className={styles.whatsappPreview__header}
        sx={{ backgroundColor: getDynamicColorsBySource("whatsapp").header_bg_color, color: getDynamicColorsBySource("whatsapp").header_font_color }}
      >
        <Box className={styles.whatsappPreview__headerProfile}>
          <Box>
            {renderUserAvatarDp(wabaName, true)}
          </Box>

          <Box className={styles.whatsappPreview__headerTitleWrap}>
            <Typography variant="subtitle1" className={styles.whatsappPreview__headerTitle}>
              {wabaName}
            </Typography>
          </Box>
        </Box>

        {(editable && !isAuthenticationTemplate) && (
          <Box className={styles.whatsappPreview__headerActions}>
            <TouchTooltip
                placement={isMobileScreen ? 'bottom-start' : 'right-end'}
                title={getTooltipTextForMessageGuidelines()}
            >
                <InfoOutlinedIcon className={styles.whatsappPreview__guidelinesInfoIcon} />
            </TouchTooltip>
          </Box>
        )}
      </Box>


      <Box className={`${styles.whatsappPreview__messageContainer}`} sx={{ backgroundImage: `url(${whatsAppDefaultBgImg})` }}>
        <Box
          className={`${styles.whatsappPreview__messageBubble} ${!(editable || templateForBubble?.headerType !== 'none') ? ` ${styles['whatsappPreview__messageBubble--noHeader']}` : ''}`}
        >

          {(editable || (templateForBubble?.headerType !== 'none')) && needToShowHeader(createTemplatePayload, interactiveTypeList, whatsappTemplateTypes, templateLibraryCategoryList) && (
            <WhatsAppMessageHeaderPreview
              template={templateForBubble}
              editable={editable}
              handleChangeText={handleChangeText}
              handleAddBodyVariableManually={handleAddBodyVariableManually}
              removeVariableData={removeVariableData}
              handleChange={handleChange}
              handleRemoveField={handleRemoveField}
              headerTextareaRef={headerTextareaRef}
              uploadFileHandler={uploadFileHandler}
              removeFileHandler={removeFileHandler}
              onChangeOfHeader={onChangeOfHeader}
              isFileUploaded={isFileUploaded}
              compact={compact}
            />
          )}

          {(editable || (templateForBubble?.body !== undefined && templateForBubble?.body !== null)) ? (
            <WhatsAppMessageBodyPreview
              editable={editable}
              displayBody={templateForBubble?.body ?? ""}
              displayBodyVariableData={templateForBubble?.bodyVariableData ?? []}
              handleAddBodyVariableManually={handleAddBodyVariableManually}
              removeVariableData={removeVariableData}
              handleChangeText={handleChangeText}
              handleChange={handleChange}
              handleRemoveField={handleRemoveField}
              textareaRef={textareaRef}
              handleFormatting={handleFormatting}
            />
          ) : null}

          {(editable || (templateForBubble?.footer)) && (
            <WhatsAppMessageFooterPreview 
              template={templateForBubble} 
              editable={editable} 
              handleChangeText={handleChangeText}
              handleExpirationChange={handleExpirationChange}
              handleSecurityRecommendationChange={handleSecurityRecommendationChange}
              handleAddExpiryTimeChange={handleAddExpiryTimeChange}
            />
          )}

        </Box>

        {showButtonsPreview && (
            <WhatsAppMessageButtonsPreview 
              template={templateForBubble}
              editable={editable}
              anchorEl={anchorEl}
              menuPosition={menuPosition}
              handleClick={handleClick}
              checkDisableAddButton={checkDisableAddButton}
              calculateMenuPosition={calculateMenuPosition}
              getMenuDisableFlag={getMenuDisableFlag}
              handleClose={handleClose}
              handleAddActionsButton={handleAddActionsButton}
              renderCustomButtonField={renderCustomButtonField}
              getInput={getInput}
              flagShowVariableTooltipForVisitWebsite={flagShowVariableTooltipForVisitWebsite}
              handleDeleteActionBtn={handleDeleteActionBtn}
              setOnChangeOfPayloadObject={setOnChangeOfPayloadObject}
              checkForAddDynamicVar={checkForAddDynamicVar}
              getWebsiteUrlValue={getWebsiteUrlValue}
              handleChangeCallField={handleChangeCallField}
              handleChangeFieldVal={handleChangeFieldVal}
              isFlowButton={isFlowButton}
              setPayloadOnChangeOfGetInput={setPayloadOnChangeOfGetInput}
              handleSampleTextChange={handleSampleTextChange}
              inValidId={inValidId}
              carouselInvalidId={carouselInvalidId}
              drawerContainerRef={previewContainerRef}
              displayTemplatePayload={displayTemplate}
            />
        )}

      </Box>

      </Box>
    </Card>
  );
};

export default WhatsAppTemplatePreview;